import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface TemplateVersion {
  id: number;
  version: number;
  subject?: string;
  body: string;
  created_by?: string;
  created_at: string;
  is_active: 0 | 1;
  change_note?: string;
}

export interface TemplateDetail {
  id: number;
  channel: "email" | "sms" | "telegram";
  code?: string;
  name: string;
  purpose?: string;
  status?: string;
  current_version_id: number;
  updated_at: string;
  updated_by?: string;
  current_version: TemplateVersion | null;
  versions: TemplateVersion[];
}

export function useTemplate(templateId: number | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["template", templateId],
    queryFn: async () => {
      const res = await fetch(`/api/cockpit/templates/${templateId}`, {
        headers,
      });
      if (!res.ok) throw new Error("Template load failed");
      return res.json() as Promise<TemplateDetail>;
    },
    enabled: !!templateId,
    staleTime: 60_000,
  });
}

export function useSaveTemplate(templateId: number | null) {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders(true);
  return useMutation({
    mutationFn: async (body: { subject?: string; body: string; change_note?: string }) => {
      const res = await fetch(`/api/cockpit/templates/${templateId}`, {
        method: "PATCH",
        headers: {
          ...headers,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        const detail = Array.isArray(payload?.errors)
          ? payload.errors.join(", ")
          : payload?.detail || payload?.error || "Template save failed";
        throw new Error(detail);
      }
      return res.json() as Promise<TemplateDetail>;
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(["template", templateId], payload);
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}
